/**
 * The Discworld quiz: which of the seven series to start with.
 *
 * Content, not interface: the questions, the options and their weights are
 * the maintainer's, from the spec of 27 September 2026 (issue #52), in
 * Bulgarian as written there. The English is a translation of it.
 *
 * The weights are the spec's. Gods was lowered one point on questions 6 and
 * 10 on 28 September to stop it being the most frequent result, and put back
 * on 29 September: the cause was the ranking, not the weights (see `rank` in
 * ../quiz.ts), and the standardised ranking fixes it without the patch.
 *
 * Changed from the spec, each with the maintainer's approval:
 *   - The reason for each option ("We chose it because …") was left for later
 *     in the spec. Drafted on 28 September for the maintainer's review. Each is
 *     said about the reader's answer, never about a book.
 *   - The Industrial Revolution's start is the maintainer's own sentence, not
 *     the path's first book: the series has two independent ways in.
 *
 * Every other series starts where its path starts, as its series page does.
 */

import type { Quiz } from '../quiz.ts'

const R = 'rincewind'
const W = 'witches'
const D = 'death'
const C = 'city-watch'
const I = 'industrial-revolution'
const G = 'gods'
const T = 'tiffany-aching'

export const discworldQuiz: Quiz = {
  world: 'discworld',
  // The spec's lead, as written. „Две, не една“ is a promise the result keeps.
  intro: {
    bg: 'Дискът се дели на седем поредици и няма правилен ред между тях — има само по-подходящо начало за различни хора. Десет въпроса, никой за самите книги: не е нужно да сте чели нищо. Накрая ще ви предложим две поредици, не една, и ще ви кажем защо.',
    en: 'The Disc falls into seven series, and there is no correct order between them — only a better starting point for different readers. Ten questions, none of them about the books: you need not have read anything. At the end we suggest two series, not one, and tell you why.',
  },
  series: [R, W, D, C, I, G, T],
  questions: [
    {
      // The anchor: weight 4, one option per series.
      prompt: { bg: 'Коя от тези истории бихте прочели първо?', en: 'Which of these stories would you read first?' },
      options: [
        {
          label: {
            bg: 'Страхливец, който бяга през половин свят, а нещастията го следват',
            en: 'A coward who runs across half the world, with misfortune at his heels',
          },
          scores: { [R]: 4 },
          reason: { bg: 'ви привлича страхливецът, който бяга', en: 'the coward on the run draws you' },
        },
        {
          label: {
            bg: 'Три жени в планинско село, които нямат власт над никого и решават всичко',
            en: 'Three women in a mountain village who hold power over nobody and settle everything',
          },
          scores: { [W]: 4 },
          reason: { bg: 'ви привличат трите жени в планинското село', en: 'the three women in the mountain village draw you' },
        },
        {
          label: {
            bg: 'Някой, който върши най-нежеланата работа на света и започва да се привързва към хората',
            en: 'Someone who does the least wanted job in the world and starts to grow fond of people',
          },
          scores: { [D]: 4 },
          reason: { bg: 'ви привлича онзи, който върши най-нежеланата работа', en: 'the one doing the least wanted job draws you' },
        },
        {
          label: {
            bg: 'Пияница, който трябва да опази град, който не иска да бъде опазван',
            en: 'A drunk who has to protect a city that does not want protecting',
          },
          scores: { [C]: 4 },
          reason: { bg: 'ви привлича пияницата, който пази града', en: 'the drunk guarding the city draws you' },
        },
        {
          label: {
            bg: 'Измамник, на когото дават да управлява държавна институция',
            en: 'A swindler who is handed a state institution to run',
          },
          scores: { [I]: 4 },
          reason: { bg: 'ви привлича измамникът начело на държавна институция', en: 'the swindler put in charge of an institution draws you' },
        },
        {
          label: {
            bg: 'Момиче, което решава да стане вещица и открива, че занаятът е предимно работа',
            en: 'A girl who decides to become a witch and finds the craft is mostly work',
          },
          scores: { [T]: 4 },
          reason: { bg: 'ви привлича момичето, което избира да стане вещица', en: 'the girl who chooses to become a witch draws you' },
        },
        {
          label: { bg: 'Бог, останал с един-единствен истински вярващ', en: 'A god left with a single true believer' },
          scores: { [G]: 4 },
          reason: { bg: 'ви привлича богът с един-единствен вярващ', en: 'the god with a single believer draws you' },
        },
      ],
    },
    {
      prompt: {
        bg: 'Някой с власт ви нарежда нещо законно, но погрешно.',
        en: 'Someone in authority orders you to do something lawful but wrong.',
      },
      options: [
        {
          label: { bg: 'Изпълнявам, после оправям щетите', en: 'I comply, then repair the damage' },
          scores: { [C]: 2 },
          reason: { bg: 'изпълнявате и после оправяте щетите', en: 'you comply and then repair the damage' },
        },
        {
          label: { bg: 'Отказвам на място', en: 'I refuse on the spot' },
          scores: { [W]: 3 },
          reason: { bg: 'отказвате на място', en: 'you refuse on the spot' },
        },
        {
          label: { bg: 'Намирам пролука в самата заповед', en: 'I find a loophole in the order itself' },
          scores: { [I]: 3 },
          reason: { bg: 'търсите пролуката в заповедта', en: 'you look for the loophole in the order' },
        },
        {
          label: { bg: 'Правя се, че не съм чул, и се махам', en: "I pretend I didn't hear, and leave" },
          scores: { [R]: 3 },
          reason: { bg: 'предпочитате да се измъкнете', en: 'you would rather slip away' },
        },
      ],
    },
    {
      prompt: { bg: 'Кое ви разсмива най-много?', en: 'What makes you laugh most?' },
      options: [
        {
          label: { bg: 'Чистият абсурд', en: 'Pure absurdity' },
          scores: { [R]: 3 },
          reason: { bg: 'ви разсмива чистият абсурд', en: 'pure absurdity makes you laugh' },
        },
        {
          label: { bg: 'Точната дума на точното място', en: 'The exact word in the exact place' },
          scores: { [D]: 2, [G]: 2 },
          reason: { bg: 'цените точната дума на точното място', en: 'you value the exact word in the exact place' },
        },
        {
          label: {
            bg: 'Когато някой каже истината, която всички избягват',
            en: 'When someone says the truth everyone avoids',
          },
          scores: { [W]: 2, [C]: 2 },
          reason: { bg: 'обичате някой да каже неудобната истина', en: 'you like it when someone says the awkward truth' },
        },
        {
          label: { bg: 'Когато внимателният план се разпадне', en: 'When a careful plan falls apart' },
          scores: { [I]: 3 },
          reason: { bg: 'ви разсмива разпадащият се план', en: 'a careful plan falling apart makes you laugh' },
        },
      ],
    },
    {
      prompt: {
        bg: 'Нова машина пристига в града и ще промени всичко.',
        en: 'A new machine arrives in town, and it will change everything.',
      },
      options: [
        {
          label: { bg: 'Вълнуващо', en: 'Exciting' },
          scores: { [I]: 3 },
          reason: { bg: 'новата машина ви вълнува', en: 'the new machine excites you' },
        },
        {
          label: { bg: 'Тревожно', en: 'Worrying' },
          scores: { [W]: 2 },
          reason: { bg: 'новата машина ви тревожи', en: 'the new machine worries you' },
        },
        {
          label: { bg: 'Зависи кой я държи', en: 'It depends who holds it' },
          scores: { [C]: 3 },
          reason: { bg: 'питате кой държи машината', en: 'you ask who holds the machine' },
        },
        {
          label: { bg: 'Ще се счупи още първия ден', en: 'It will break on the first day' },
          scores: { [R]: 2 },
          reason: { bg: 'не вярвате, че машината ще издържи', en: 'you doubt the machine will last' },
        },
      ],
    },
    {
      prompt: { bg: 'Кое е по-трудно?', en: 'Which is harder?' },
      options: [
        {
          label: { bg: 'Да се научиш на занаят', en: 'Learning a trade' },
          scores: { [T]: 3 },
          reason: { bg: 'знаете колко трудно се учи занаят', en: 'you know how hard a trade is to learn' },
        },
        {
          label: {
            bg: 'Да поемеш отговорност, за която не си готов',
            en: 'Taking on a responsibility you are not ready for',
          },
          scores: { [T]: 2, [C]: 2 },
          reason: { bg: 'познавате отговорността, за която не сте готови', en: 'you know responsibility you are not ready for' },
        },
        {
          label: { bg: 'Да си единственият, който вижда проблема', en: 'Being the only one who sees the problem' },
          scores: { [W]: 3, [D]: 2 },
          reason: { bg: 'познавате самотата на онзи, който вижда проблема', en: 'you know what it is to be the only one who sees the problem' },
        },
        {
          label: { bg: 'Да продължиш, след като си се провалил', en: 'Carrying on after you have failed' },
          scores: { [R]: 2, [I]: 2 },
          reason: { bg: 'цените продължаването след провал', en: 'you value carrying on after a failure' },
        },
      ],
    },
    {
      prompt: {
        bg: 'Бог, в когото вече никой не вярва истински — какво му остава?',
        en: 'A god nobody truly believes in any more: what is it left with?',
      },
      options: [
        {
          label: { bg: 'Нищо', en: 'Nothing' },
          scores: { [G]: 3 },
          reason: { bg: 'мислите, че на забравения бог не остава нищо', en: 'you think a forgotten god is left with nothing' },
        },
        {
          label: { bg: 'Точно толкова, колкото и преди', en: 'Exactly as much as before' },
          scores: { [D]: 2, [G]: 1 },
          reason: { bg: 'мислите, че на бога остава толкова, колкото и преди', en: 'you think the god keeps as much as before' },
        },
        {
          label: { bg: 'Зависи от един човек', en: 'It depends on one person' },
          scores: { [G]: 3, [W]: 1 },
          reason: { bg: 'мислите, че всичко зависи от един човек', en: 'you think it all comes down to one person' },
        },
        { label: { bg: 'Не съм мислил за това', en: 'I have never thought about it' }, scores: {}, reason: null },
      ],
    },
    {
      prompt: { bg: 'Къде се чувствате по-у дома?', en: 'Where do you feel more at home?' },
      options: [
        {
          label: { bg: 'Където всички се познават', en: 'Where everyone knows everyone' },
          scores: { [W]: 3, [T]: 3 },
          reason: { bg: 'сте у дома там, където всички се познават', en: 'you are at home where everyone knows everyone' },
        },
        {
          label: { bg: 'Където никой не ви познава', en: 'Where nobody knows you' },
          scores: { [C]: 3, [I]: 2 },
          reason: { bg: 'сте у дома там, където никой не ви познава', en: 'you are at home where nobody knows you' },
        },
        { label: { bg: 'Все едно', en: 'Either' }, scores: {}, reason: null },
      ],
    },
    {
      prompt: { bg: 'Какво би ви накарало да зарежете една поредица?', en: 'What would make you drop a series?' },
      options: [
        {
          label: { bg: 'Ако стане твърде сериозна', en: 'If it got too serious' },
          scores: { [R]: 3 },
          reason: { bg: 'не искате поредицата да стане твърде сериозна', en: 'you do not want a series to turn too serious' },
        },
        {
          label: { bg: 'Ако остане само шега', en: 'If it stayed only a joke' },
          scores: { [C]: 3, [D]: 2 },
          reason: { bg: 'искате повече от шега', en: 'you want more than a joke' },
        },
        {
          label: { bg: 'Ако героите не се променят', en: 'If the characters never changed' },
          scores: { [T]: 3, [C]: 2 },
          reason: { bg: 'искате героите да се променят', en: 'you want the characters to change' },
        },
        {
          label: { bg: 'Ако стане твърде дълга', en: 'If it got too long' },
          scores: { [G]: 3 },
          reason: { bg: 'не искате поредицата да е твърде дълга', en: 'you do not want a series to run too long' },
        },
      ],
    },
    {
      prompt: { bg: 'Как четете?', en: 'How do you read?' },
      options: [
        {
          label: { bg: 'Една книга наведнъж, докрай', en: 'One book at a time, to the end' },
          scores: { [G]: 2 },
          reason: { bg: 'четете една книга наведнъж, докрай', en: 'you read one book at a time, to the end' },
        },
        {
          label: { bg: 'Пробвам няколко и виждам коя хваща', en: 'I try a few and see which one takes' },
          scores: { [R]: 2 },
          reason: { bg: 'пробвате няколко и виждате коя хваща', en: 'you try a few and see which one takes' },
        },
        {
          label: { bg: 'Стриктно по ред, от началото', en: 'Strictly in order, from the start' },
          scores: { [C]: 2, [T]: 2 },
          reason: { bg: 'четете стриктно по ред', en: 'you read strictly in order' },
        },
        {
          label: { bg: 'Прескачам и се връщам', en: 'I skip about and come back' },
          scores: { [W]: 1, [D]: 1 },
          reason: { bg: 'прескачате и се връщате', en: 'you skip about and come back' },
        },
      ],
    },
    {
      prompt: { bg: 'Колко дълго искате да продължи?', en: 'How long do you want it to last?' },
      options: [
        {
          label: { bg: 'Една книга', en: 'One book' },
          scores: { [G]: 3 },
          reason: { bg: 'искате една книга', en: 'you want a single book' },
        },
        {
          label: { bg: 'Пет-шест', en: 'Five or six' },
          scores: { [D]: 2, [W]: 2, [T]: 2, [I]: 2 },
          reason: { bg: 'искате пет-шест книги', en: 'you want five or six books' },
        },
        {
          label: { bg: 'Колкото има', en: 'As many as there are' },
          scores: { [C]: 3, [R]: 2 },
          reason: { bg: 'искате колкото има', en: 'you want as many as there are' },
        },
      ],
    },
  ],
  starts: {
    // The maintainer's sentence (28 September 2026), as written, with the
    // typo they corrected („Двете са независими“) and the commas Bulgarian
    // puts before „ако“. {a} is Moving Pictures, {b} is Going Postal; the
    // page renders each as the book's title, linked to its page.
    [I]: {
      text: {
        bg: '{a}, ако бихте искали да започнете с Индустриална революция на Анкх-Морпорк, или {b}, ако искате директно да се сблъскате с Мокр фон Ментебрад. Двете са независими и няма грешен избор.',
        en: '{a} if you would like to begin with the Industrial Revolution of Ankh-Morpork, or {b} if you want to meet Moist von Lipwig head on. The two are independent, and there is no wrong choice.',
      },
      works: ['moving-pictures', 'going-postal'],
    },
  },
}
